import React from 'react'
import { Card, List, Typography, Button, Switch, Image, Space, Tooltip } from 'antd'
import { EditOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import { FlattenedRoomData } from './types'
import { renderTags } from './utils'
import htmlToPlainText from '@/components/HtmlToPlainText'
import { DEFAULT_ROOM_IMAGE } from '@/constants/AppConfig'

const { Text, Paragraph } = Typography

type RoomMobileListProps = {
  rooms: FlattenedRoomData[]
  loading: boolean
  updatingRoomCode: string | null
  isSortMode: boolean
  onShowDetail: (room: FlattenedRoomData) => void
  onEdit: (room: FlattenedRoomData) => void
  onToggleEnabled: (room: FlattenedRoomData, enabled: boolean) => void
  onMoveUp: (roomCode: string) => void
  onMoveDown: (roomCode: string) => void
}

const RoomMobileList: React.FC<RoomMobileListProps> = ({
  rooms,
  loading,
  updatingRoomCode,
  isSortMode,
  onShowDetail,
  onEdit,
  onToggleEnabled,
  onMoveUp,
  onMoveDown,
}) => {
  return (
    <List
      grid={{ gutter: 16, xs: 1, sm: 1 }}
      dataSource={rooms}
      loading={loading}
      renderItem={(room, index) => (
        <List.Item>
          <Card
            hoverable={!isSortMode}
            style={{ width: '100%' }}
            onClick={() => !isSortMode && onShowDetail(room)}
            cover={
              <div style={{ position: 'relative' }}>
                <Image
                  alt={room.display_name}
                  src={room.cover_image || DEFAULT_ROOM_IMAGE}
                  width="100%"
                  style={{ height: 180, objectFit: 'cover' }}
                  preview={false}
                />
                {isSortMode && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      background: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      borderRadius: 4,
                      padding: '2px 8px',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    #{index + 1}
                  </div>
                )}
              </div>
            }
          >
            <Card.Meta
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>{room.display_name}</Text>
                    {(room.max_avail != null || room.avail_offset != null) && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                        {room.max_avail != null && (
                          <Text type="secondary" style={{ fontSize: 11 }}>Max: {room.max_avail}</Text>
                        )}
                        {room.avail_offset != null && (
                          <Text type="secondary" style={{ fontSize: 11 }}>Offset: {room.avail_offset}</Text>
                        )}
                      </div>
                    )}
                  </div>
                  {isSortMode ? (
                    <Space onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Move Up">
                        <Button
                          size="small"
                          icon={<ArrowUpOutlined />}
                          disabled={index === 0}
                          onClick={() => onMoveUp(room.room_code)}
                        />
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <Button
                          size="small"
                          icon={<ArrowDownOutlined />}
                          disabled={index === rooms.length - 1}
                          onClick={() => onMoveDown(room.room_code)}
                        />
                      </Tooltip>
                    </Space>
                  ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={room.enabled ? 'Disable Room' : 'Enable Room'}>
                        <Switch
                          checked={room.enabled}
                          onChange={(checked) => onToggleEnabled(room, checked)}
                          loading={updatingRoomCode === room.room_code}
                          size="small"
                          style={{ width: 40 }}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              }
              description={<Text type="secondary" style={{ fontSize: '12px' }}>{room.room_code}</Text>}
            />

            <Paragraph ellipsis={{ rows: 2, expandable: false }} style={{ marginTop: 16 }}>
              {htmlToPlainText(room.description)}
            </Paragraph>

            <div style={{ marginBottom: 16 }}>
              {room.highlights && room.highlights.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {renderTags(room.highlights.slice(0, 3), 'geekblue', Math.max(room.highlights.length - 3, 0))}
                </div>
              )}
              {room.amenities && room.amenities.length > 0 && (
                <div>
                  {renderTags(room.amenities.slice(0, 3), 'green', Math.max(room.amenities.length - 3, 0))}
                </div>
              )}
            </div>

            {!isSortMode && (
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); onEdit(room) }}>Edit</Button>
              </Space>
            )}
          </Card>
        </List.Item>
      )}
    />
  )
}

export default RoomMobileList
